package com.tovtalk.domain.entity

import com.tovtalk.data.network.VideoQuality
import kotlin.time.Instant

data class Post(
    val id: Int,
    val ref: String,
    val type: PostType = PostType.VIDEO,
    val caption: String? = null,
    val hashtags: List<String> = emptyList(),

    // Video - selected quality + all sources
    val videoUrl: String,
    val videoQuality: VideoQuality = VideoQuality.MID,
    val allSources: Map<String, String> = emptyMap(),
    val videoSizes: Map<String, Int>? = null,
    val thumbnail: String? = null,
    val duration: Int? = null,

    // Stats (grouped)
    val stats: PostStats = PostStats(),

    // Author (replaces user: User?)
    val author: PostAuthor,

    // Viewer engagement state
    val viewerState: ViewerState = ViewerState(),

    // Music
    val music: Music? = null,

    // Timestamps (keep Instant)
    val createdAt: Instant,
    val originalVideoTime: Instant? = null,

    // Expiration
    val expiresAt: Instant? = null,
    val isExpired: Boolean = false,
    val isPermanent: Boolean = true,
    val isPinned: Boolean = false,
    val uploadedViaAdmin: Boolean = false
)

data class PostStats(
    val views: Long = 0,
    val likes: Long = 0,
    val comments: Long = 0,
    val saves: Long = 0,
    val shares: Long = 0
)

data class PostAuthor(
    val id: Int,
    val username: String,
    val fullname: String? = null,
    val profilePhoto: String? = null,
    val hasActiveStory: Boolean = false
)

data class ViewerState(
    val isLiked: Boolean = false,
    val isSaved: Boolean = false,
    val isFollowing: Boolean = false
)

/**
 * Toggle this post's like state. [wasLiked] is the like state BEFORE the action
 * (i.e. the value passed to the like API). Result reflects the new (toggled) state.
 * Shared by Home/Profile/PostDetail ViewModels so the merge logic lives in one place.
 */
fun Post.toggleLike(wasLiked: Boolean): Post = copy(
    viewerState = viewerState.copy(isLiked = !wasLiked),
    stats = stats.copy(likes = if (wasLiked) (stats.likes - 1).coerceAtLeast(0) else stats.likes + 1)
)

/** Apply [toggleLike] to the post with [postId] within this list, leaving others untouched. */
fun List<Post>.toggleLike(postId: Int, wasLiked: Boolean): List<Post> =
    map { if (it.id == postId) it.toggleLike(wasLiked) else it }

enum class PostType(val value: Int, val apiValue: String) {
    REEL(1, "reel"),
    VIDEO(2, "video");

    companion object {
        fun fromInt(value: Int) = entries.firstOrNull { it.value == value } ?: VIDEO
        fun fromString(value: String) = entries.firstOrNull { it.apiValue == value } ?: VIDEO
    }
}

data class Music(
    val id: Int,
    val title: String,
    val artist: String,
    val audioUrl: String,
    val thumbnail: String?,
    val duration: Int,
    val categoryId: Int? = null
)

data class MusicCategory(
    val id: Int,
    val name: String,
    val icon: String?
)

// Updated for cursor pagination
data class UserReelsResult(
    val posts: List<Post>,
    val nextCursor: String? = null,
    val kashrutRestricted: Boolean = false
)
